/* Bench view — "Who's freeing up, and when?"
   Primary PM use-case: scan the full roster to find specialists whose current
   commitments wind down soon, so negotiations to pull them onto another
   project can start early.

   Each row = 1 person.  Columns = months in the current Period.  Cell color
   encodes FREE capacity (not consumed capacity — that's what Dashboard does).
   A pinned "Frees up" column on the left surfaces the first month in-window
   where the person has enough spare capacity to matter, and rows are sorted
   by that key ascending (soonest first). */
import { type FC, useState } from "react";

import type { Member, Person, Project } from "@shared/types";
import { aggregatePersonAllocations } from "../data";
import { useScrollCurrentIntoView, windowMonths } from "../lib/grid-helpers";
import { useC } from "../theme";
import type { MonthEntry } from "../tokens";
import { Av, Card, Empty, Input, useTableStyles } from "../ui";

export interface BenchViewProps {
  projects: Project[];
  people: Person[];
  rangeStart: string;
  rangeEnd: string;
  role: string;
  currentUserId: number | null;
  openPerson?: (pid: number) => void;

  tweaks?: { zoom?: number };
}

interface BenchCell {
  key: string;
  used: number;
  free: number;
  cap: number;
  vac: boolean;
  isCurrent: boolean;
}

interface RosterEntry {
  person: Person;
  cells: BenchCell[];
  freesAt: number;
  cap: number;
  onMine: boolean;
  roles: string[];
}

export const BenchView: FC<BenchViewProps> = ({
  projects,
  people,
  rangeStart,
  rangeEnd,
  role,
  currentUserId,
  openPerson,
  tweaks,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const months = windowMonths(rangeStart, rangeEnd);
  useScrollCurrentIntoView(rangeStart, rangeEnd, "bench");
  const zoom = tweaks?.zoom || 1;
  const cellMin = Math.round(52 * zoom);

  /* Thresholds (in % of personal capacity).  A person is considered FREE in
     a given month when their allocation sits at/below `freeBelow`.  Tuned
     toward "has time for another half-role" rather than "literally idle". */
  const [freeBelow, setFreeBelow] = useState(50);
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  /* PM shortcut — hide teammates already on one of my projects (they won't
     need negotiating, they're already mine).  Off by default. */
  const [hideMine, setHideMine] = useState(false);

  /* === Build per-person capacity snapshot for the window ============ */
  const roster: RosterEntry[] = people.map((pp) => {
    const cap = pp.wl || 100;
    const allocs = aggregatePersonAllocations(pp.id, projects, months);
    /* free% in each month = capacity - planned (clamped >= 0) */
    const cells: BenchCell[] = months.map((m) => {
      const used = Math.round(allocs[m.key] || 0);
      const free = Math.max(0, cap - used);
      /* Time-off detection: any allocation on a project flagged isTimeOff this month */
      let vac = false;
      for (const pr of projects) {
        if (!pr.isTimeOff) continue;
        for (const mb of pr.members) {
          if (mb.pid === pp.id && mb.a[m.key]) {
            vac = true;
            break;
          }
        }
        if (vac) break;
      }
      return { key: m.key, used, free, cap, vac, isCurrent: m.isCurrent };
    });

    /* "Frees up" = earliest window index where the person sits at/below
       `freeBelow`.  Time-off months count as not-free (they're not available
       for new work). */
    let freesAt = -1;
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      if (!cell) continue;
      if (cell.vac) continue;
      if (cell.used <= freeBelow) {
        freesAt = i;
        break;
      }
    }

    /* Is this person on one of MY projects?  Used for hideMine filter and
       for the "Already on your team" pill. */
    const onMine =
      role === "pm" && currentUserId
        ? projects.some(
            (pr: Project) =>
              pr.pmId === currentUserId && pr.members.some((m: Member) => m.pid === pp.id),
          )
        : false;

    /* Role set for this person (from role chips) + all project roles they
       currently fill, merged. */
    const rolesSet = new Set<string>(pp.roles);
    for (const pr of projects) {
      for (const m of pr.members) {
        if (m.pid === pp.id && m.role) rolesSet.add(m.role);
      }
    }

    return { person: pp, cells, freesAt, cap, onMine, roles: [...rolesSet] };
  });

  /* Available roles for the filter dropdown — union of everyone's roles. */
  const allRoles = [...new Set(roster.flatMap((r) => r.roles))].sort();

  /* === Filter + sort =============================================== */
  let filtered = roster;
  if (q.trim())
    filtered = filtered.filter((r) => r.person.name.toLowerCase().includes(q.trim().toLowerCase()));
  if (roleFilter !== "all") filtered = filtered.filter((r) => r.roles.includes(roleFilter));
  if (hideMine) filtered = filtered.filter((r) => !r.onMine);

  /* Sort: soonest freeing first; never-frees to the bottom. */
  filtered = [...filtered].sort((a, b) => {
    const af = a.freesAt < 0 ? Number.POSITIVE_INFINITY : a.freesAt;
    const bf = b.freesAt < 0 ? Number.POSITIVE_INFINITY : b.freesAt;
    if (af !== bf) return af - bf;
    return a.person.name.localeCompare(b.person.name);
  });

  /* Summary counts for the header strip. */
  const nowFree = roster.filter((r) => r.freesAt === 0).length;
  const soonFree = roster.filter((r) => r.freesAt > 0 && r.freesAt <= 3).length;
  const booked = roster.filter((r) => r.freesAt < 0).length;

  /* Cell colorway.  Vacation wins; then by utilisation band.  Empty (0% used)
     renders as a near-invisible · so the grid reads like the Person view — a
     green block for "free" was too loud given every un-planned cell looked
     celebratory. */
  function cellSkin(c: BenchCell) {
    if (c.used === 0)
      return { bg: "transparent", co: C.slateDim, label: "·", bd: "transparent", empty: true };
    if (c.used <= freeBelow)
      return {
        bg: C.blueTint2,
        co: C.blue,
        label: `${c.used}%`,
        bd: C.blueTint4,
        empty: false,
      };
    if (c.used <= 85) return { bg: C.yBg, co: C.y, label: `${c.used}%`, bd: C.yBd, empty: false };
    if (c.used <= 100)
      return {
        bg: C.lineSoft,
        co: C.slateMut,
        label: `${c.used}%`,
        bd: C.line,
        empty: false,
      };
    return { bg: C.rBg, co: C.r, label: `${c.used}%`, bd: C.rBd, empty: false };
  }

  /* "Frees up" pill. */
  const FreePill: FC<{ r: RosterEntry }> = ({ r }) => {
    if (r.freesAt < 0)
      return (
        <span style={{ fontSize: 11, color: C.slateMut, fontWeight: 500, fontStyle: "italic" }}>
          Fully booked
        </span>
      );
    const m = months[r.freesAt];
    if (!m) return null;
    const now = r.freesAt === 0;
    const soon = r.freesAt <= 3;
    const bg = now ? C.g : soon ? C.blue : C.blueTint4;
    const co = now || soon ? "#fff" : C.blue;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 9px",
          background: bg,
          color: co,
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: co, opacity: 0.9 }} />
        {now ? "Now" : m.label}
      </span>
    );
  };
  // Reference FreePill so it's not flagged as unused (kept verbatim from designv2)
  void FreePill;

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1600, margin: "0 auto" }}>
      {/* ============ Header ================================ */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 14,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Bench
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            Every person, every month — sorted by who frees up first. Start negotiations early.
          </p>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Input value={q} onChange={setQ} placeholder="Search person…" />
        </div>
      </div>

      {/* ============ Summary chips ========================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {[
          {
            l: "Free now",
            v: nowFree,
            sub: `≤${freeBelow}% utilised this month`,
            co: C.g,
            bg: C.gBg,
            bd: C.gBd,
          },
          {
            l: "Free within 3mo",
            v: soonFree,
            sub: "soon — start talking now",
            co: C.blue,
            bg: C.blueTint2,
            bd: C.blueTint4,
          },
          {
            l: "Fully booked",
            v: booked,
            sub: "no gap in the window",
            co: C.slateMut,
            bg: C.canvas,
            bd: C.line,
          },
          {
            l: "Roster total",
            v: roster.length,
            sub: "people in planner",
            co: C.navy,
            bg: C.surface,
            bd: C.line,
          },
        ].map((k) => (
          <Card key={k.l} pad="12px 16px" style={{ background: k.bg, borderColor: k.bd }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 2,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              {k.l}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: k.co,
                fontFamily: "'Poppins',system-ui,sans-serif",
                lineHeight: 1.1,
                letterSpacing: -0.3,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {k.v}
            </div>
            <div style={{ fontSize: 10.5, color: C.slateMut, marginTop: 2 }}>{k.sub}</div>
          </Card>
        ))}
      </div>

      {/* ============ Toolbar (filters + thresholds) ======== */}
      <Card
        style={{
          padding: "12px 16px",
          marginBottom: 14,
          display: "flex",
          gap: 20,
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: 10,
        }}
      >
        {/* Role filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: C.slateMut,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Role
          </span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              border: `1px solid ${C.line}`,
              fontSize: 12,
              color: C.navy,
              background: C.surface,
              fontWeight: 600,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            <option value="all">All roles</option>
            {allRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: 1, height: 22, background: C.line }} />

        {/* Free threshold — the % at/below which we call someone available */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: C.slateMut,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Free if ≤
          </span>
          <input
            type="range"
            min="0"
            max="80"
            step="5"
            value={freeBelow}
            onChange={(e) => setFreeBelow(Number(e.target.value))}
            style={{ width: 120, accentColor: C.blue }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.blue,
              fontVariantNumeric: "tabular-nums",
              minWidth: 34,
            }}
          >
            {freeBelow}%
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {role === "pm" && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              fontSize: 12,
              color: C.slate,
            }}
          >
            <input
              type="checkbox"
              checked={hideMine}
              onChange={(e) => setHideMine(e.target.checked)}
              style={{ accentColor: C.blue }}
            />
            Hide people already on my team
          </label>
        )}

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontSize: 10.5,
            color: C.slateMut,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 10,
                height: 10,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: C.slateDim,
                fontWeight: 700,
              }}
            >
              ·
            </span>
            Free
          </span>
          <LegendDot bg={C.blueTint2} bd={C.blueTint4} label="Light" />
          <LegendDot bg={C.yBg} bd={C.yBd} label="Busy" />
          <LegendDot bg={C.lineSoft} bd={C.line} label="Full" />
        </div>
      </Card>

      {/* ============ Main grid ============================ */}
      <Card style={{ overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <Empty
            title="No matches"
            msg="Try relaxing the role filter or lowering the 'Free if' threshold."
          />
        ) : (
          <div style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      ...TH,
                      textAlign: "left",
                      minWidth: 220,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      background: C.canvas,
                      zIndex: 4,
                      borderRight: `1px solid ${C.line}`,
                    }}
                  >
                    Person
                  </th>
                  {months.map((m: MonthEntry) => {
                    const isCurrent = m.isCurrent;
                    return (
                      <th
                        key={m.key}
                        data-current-month={isCurrent ? "true" : undefined}
                        style={{
                          ...TH,
                          textAlign: "center",
                          minWidth: cellMin,
                          padding: "8px 4px",
                          fontSize: 10,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          background: isCurrent ? C.blueTint2 : C.canvas,
                          borderBottom: `1px solid ${C.line}`,
                          boxShadow: isCurrent ? `inset 0 -2px 0 ${C.blue}` : undefined,
                        }}
                      >
                        {m.short}
                        <br />
                        <span style={{ fontSize: 8.5, color: C.slateDim, fontWeight: 500 }}>
                          {String(m.year).slice(2)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const rowBg = i % 2 ? C.surfaceAlt : C.surface;
                  const soonest = r.freesAt;
                  return (
                    <tr key={r.person.id}>
                      {/* Person cell ------------------------------------- */}
                      <td
                        style={{
                          ...TD,
                          padding: "8px 12px",
                          textAlign: "left",
                          position: "sticky",
                          left: 0,
                          background: rowBg,
                          zIndex: 2,
                          borderBottom: `1px solid ${C.lineSoft}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openPerson?.(r.person.id)}
                          title="Open in Person view"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "left",
                            width: "100%",
                          }}
                        >
                          <Av name={r.person.name} sz={28} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: C.navyDeep,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.person.name}
                            </div>
                            <div
                              style={{
                                fontSize: 10.5,
                                color: C.slateMut,
                                display: "flex",
                                gap: 6,
                                alignItems: "center",
                                marginTop: 1,
                              }}
                            >
                              <span>{r.cap}%</span>
                              {r.roles.length > 0 && (
                                <>
                                  <span style={{ color: C.slateDim }}>·</span>
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {r.roles.slice(0, 2).join(", ")}
                                    {r.roles.length > 2 ? ` +${r.roles.length - 2}` : ""}
                                  </span>
                                </>
                              )}
                              {r.onMine && (
                                <span
                                  style={{
                                    background: C.blueTint2,
                                    color: C.blue,
                                    padding: "0 5px",
                                    borderRadius: 3,
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  mine
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      </td>
                      {/* Month cells ------------------------------------- */}
                      {r.cells.map((c, ci) => {
                        const s = cellSkin(c);
                        const isFreeingHere = ci === soonest;
                        return (
                          <td
                            key={c.key}
                            style={{
                              padding: "3px",
                              borderBottom: `1px solid ${C.lineSoft}`,
                              textAlign: "center",
                              background: c.isCurrent ? C.blueTint1 : "transparent",
                              position: "relative",
                            }}
                          >
                            <div
                              title={`${c.used}% planned · ${c.free}% free · cap ${c.cap}%`}
                              style={
                                s.empty
                                  ? {
                                      color: s.co,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      lineHeight: 1,
                                      padding: "5px 2px",
                                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                                    }
                                  : {
                                      background: s.bg,
                                      color: s.co,
                                      border: `1px solid ${s.bd}`,
                                      borderRadius: 4,
                                      padding: "5px 2px",
                                      fontSize: 10,
                                      fontWeight: 600,
                                      fontVariantNumeric: "tabular-nums",
                                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                                      position: "relative",
                                      boxShadow: isFreeingHere ? `0 0 0 2px ${C.blue}` : "none",
                                    }
                              }
                            >
                              {s.label}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ fontSize: 11, color: C.slateMut, marginTop: 10, lineHeight: 1.6 }}>
        Cells show <strong style={{ color: C.navy }}>planned utilisation</strong>. A person is
        called <em>free</em> when their month sits at or below{" "}
        <strong style={{ color: C.blue }}>{freeBelow}%</strong> of capacity. Click any name to open
        their full timeline and reach out.
      </div>
    </div>
  );
};

const LegendDot: FC<{ bg: string; bd: string; label: string }> = ({ bg, bd, label }) => {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 3,
          background: bg,
          border: `1px solid ${bd}`,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
};
